import {
  getBodyAgeGroup,
  getBodyImage,
  getOrganImages,
  type OrganArtworkPart,
} from '#assets/images/gutXAxis';
import type { GutXAxisOrgan, OrganLookupCode } from '@repo/types';
import { getAgeGroup } from '@repo/utils';

export type GutXAxisSystem = {
  organ: GutXAxisOrgan;
  parts: OrganArtworkPart[];
};

/**
 * Figma's head-down reading order. `organs.priority` cannot serve: science sets
 * that by prominence, and the map paints in that order. Post-MVP codes are listed
 * too, so the order is right the day their artwork lands.
 */
const ORGAN_DISPLAY_ORDER: OrganLookupCode[] = [
  'brain',
  'oral',
  'endocrine',
  'immune',
  'skin',
  'lung',
  'heart',
  'gut',
  'liver',
  'metabolic',
  'muscle',
  'reproductive',
  'kidney',
];

/** An unknown code sorts last, where `indexOf`'s -1 would put it first. */
const rank = (lookupCode: OrganLookupCode) => {
  const index = ORGAN_DISPLAY_ORDER.indexOf(lookupCode);
  return index === -1 ? ORGAN_DISPLAY_ORDER.length : index;
};

const byOrganDisplayOrder = (a: GutXAxisSystem, b: GutXAxisSystem) =>
  rank(a.organ.lookup_code) - rank(b.organ.lookup_code);

type ResolveGutXAxisSystemsArgs = {
  organs: GutXAxisOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /**
   * The codes the *body* draws, where that is not `organs` itself: the gut is
   * recut around the liver and kidneys, so a caller rendering one organ out of
   * the set still has to pass the whole set.
   */
  drawnCodes?: ReadonlySet<OrganLookupCode>;
};

/**
 * The single answer to which organs a kit's body can draw, so no surface can name
 * a system the others do not: `getOrganImages` returns nothing for `oral`, for
 * adult-only organs on a younger body, or for a uterus on a male.
 *
 * `systems` keeps the order the organs arrived in, which the map paints in — they
 * are absolutely positioned at one z-index, so DOM order is stacking order and the
 * gut has to stay under the liver. `listedSystems` is the reading order.
 */
export function resolveGutXAxisSystems({
  organs,
  age,
  pregnant,
  sex,
  drawnCodes: drawnCodesOverride,
}: ResolveGutXAxisSystemsArgs) {
  const ageGroup = getAgeGroup(age);
  const bodyAgeGroup = getBodyAgeGroup({ age, pregnant });

  const drawnCodes =
    drawnCodesOverride ?? new Set(organs.map(organ => organ.lookup_code));

  const systems = organs
    .map(organ => ({
      organ,
      parts: getOrganImages({
        ageGroup,
        sex,
        pregnant,
        drawnCodes,
        flagLevel: organ.flag_level,
        lookupCode: organ.lookup_code,
      }),
    }))
    .filter(system => system.parts.length > 0);

  return {
    body: getBodyImage(bodyAgeGroup),
    bodyAgeGroup,
    drawnCodes,
    systems,
    listedSystems: [...systems].sort(byOrganDisplayOrder),
  };
}
